/** @jsxImportSource @emotion/react */
import { useLayerDataFetcher } from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDataFetcher'
import { BaseMapButton } from '@app/components/Pages/Map/BaseMapButton'
import { MapLegends } from '@app/components/Pages/Map/MapLegends'
import { PropertyMapTools } from '@app/components/Pages/Map/MapTools/MapTools'
import { PropertySearch } from '@app/components/Property/components/PropertySearch'
import {
  selectedAssetFeatureAtom,
  selectedAssetIdAtom,
  shownHazardLayersAtom,
} from '@app/state/propertyAtoms'
import { propertyMapInstanceAtom, propertyMapPaddingRefAtom } from '@app/state/propertyMapAtoms'
import { css } from '@emotion/react'
import { Box, useTheme } from '@mui/material'
import { useAtomValue, useSetAtom } from 'jotai'
import mapboxgl, { Map, MapboxGeoJSONFeature, MapLayerMouseEvent, RasterLayer } from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { useEffect, useMemo, useRef } from 'react'
import ReactDOMServer from 'react-dom/server'
import { useParams } from 'react-router-dom'
import { GenericMapView } from '..'
import { MapMouseEventManager } from '../GenericMapView/GenericMapView.utilities'
import {
  mapBottomLeftContainer,
  mapBottomRightContainer,
  mapOverlayContainer,
  mapTopRightContainer,
} from './PropertyMapView.styles'
import { PropertyMapTooltip } from './Tooltip/PropertyMapTooltip'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN

export const PropertyMapView = () => {
  mapboxgl.accessToken = MAPBOX_TOKEN
  const theme = useTheme()
  const { id } = useParams<string>()

  const layerData = useLayerDataFetcher()
  const selectedAssetFeature = useAtomValue(selectedAssetFeatureAtom)
  const shownHazardLayers = useAtomValue(shownHazardLayersAtom)
  const setSelectedAssetId = useSetAtom(selectedAssetIdAtom)
  const propertyLayerId = 'selected-property-layer'

  useEffect(() => {
    if (id) {
      setSelectedAssetId(id)
    }
  }, [id, setSelectedAssetId])

  // Creates an object of form { tilesetId: [legend1, legend2, ...] }
  const legendByHazardTileset = useMemo(() => {
    return layerData?.hazardDialogData?.hazardDetails?.reduce(
      (acc, hazard) => {
        hazard.scenarios.forEach((scenario) => {
          scenario.tilesets.forEach((tileset) => {
            if (!acc[tileset.id]) {
              acc[tileset.id] = []
            }
            if (scenario.legend && !acc[tileset.id].includes(scenario.legend)) {
              acc[tileset.id].push(scenario.legend)
            }
          })
        })

        return acc
      },
      {} as Record<string, string[]>,
    )
  }, [layerData])

  const legendIds = useMemo(() => {
    const hazardLayerLegends = shownHazardLayers.flatMap((layerId) => {
      return legendByHazardTileset?.[layerId] || []
    })

    return hazardLayerLegends
  }, [legendByHazardTileset, shownHazardLayers])

  const map = useAtomValue(propertyMapInstanceAtom)
  const setMapInstance = useSetAtom(propertyMapInstanceAtom)
  const mapPaddingRef = useAtomValue(propertyMapPaddingRefAtom)

  const tooltipRef = useRef(
    new mapboxgl.Popup({ closeButton: false, className: 'prioritisation-tooltip-container' }),
  )
  const mapTopRightControlsRef = useRef<HTMLDivElement>(null)

  const tooltipFeature = useRef<MapboxGeoJSONFeature | undefined>()
  const tooltipTimeout = useRef<NodeJS.Timeout>()

  const mapMouseEventManager = map ? new MapMouseEventManager(map) : null

  async function controlMouseEvents(map: Map) {
    addMouseEvents(map)
  }

  async function addMouseEvents(map: Map) {
    const popup = tooltipRef.current
    const mapStyle = map.getStyle()
    if (!mapStyle || !popup || !mapMouseEventManager || !selectedAssetFeature) return

    const layerIdMap = mapStyle.layers
      .filter((layer) => propertyLayerId == layer.id)
      .map((layer) => layer.id)

    const updateTooltip = (e: MapLayerMouseEvent) => {
      if (!e.features || !e.features.length) return
      const feature = e.features[0]

      const tooltipContent = feature.properties?.toString()
      popup.setLngLat(e.lngLat)

      // return if no content or already hovered over
      if (!tooltipContent || (tooltipFeature.current && tooltipFeature.current.id === feature.id))
        return
      tooltipFeature.current = e.features[0]

      map.getCanvas().style.cursor = 'pointer'

      // Fade out outdated tooltip content (this typecast is safe btw)
      const htmlContent = document.getElementsByClassName('tooltip-container')[0] as HTMLElement
      if (htmlContent) htmlContent.style.color = '#AAA'

      const mapToolTip = PropertyMapTooltip({
        areaName: feature.properties?.name,
        properties: feature.properties,
      })

      if (!mapToolTip) return
      popup.setHTML(ReactDOMServer.renderToString(mapToolTip))
      popup.addTo(map)
    }

    const mouseEnterEvent = function (e: MapLayerMouseEvent) {
      if (!e) return
      updateTooltip(e)
    }

    const mouseMoveEvent = (e: MapLayerMouseEvent) => {
      updateTooltip(e)
    }

    const mouseLeaveEvent = (_e: MapLayerMouseEvent) => {
      if (!map) return
      clearTimeout(tooltipTimeout.current)
      tooltipRef.current.remove()
      tooltipFeature.current = undefined
      map.getCanvas().style.cursor = ''
    }

    mapMouseEventManager.applyMouseEventsToLayers(layerIdMap, {
      onMouseEnter: mouseEnterEvent,
      onMouseMove: mouseMoveEvent,
      onMouseLeave: mouseLeaveEvent,
    })
  }

  useEffect(() => {
    async function addMapLayersAndEvents() {
      if (!map || !selectedAssetFeature) return

      const mapStyle = map.getStyle()
      const mapLayers = mapStyle?.layers

      if (!mapLayers) return

      const uiTilesets = mapStyle.layers.filter(
        (layer) =>
          layer.type === 'raster' &&
          layer.id.includes('urbanintelligence') &&
          !shownHazardLayers.includes(layer.id),
      )

      // Remove hazard layers that are no longer in shownHazardLayers
      uiTilesets.forEach((layer) => {
        if (map.getLayer(layer.id)) {
          map.removeLayer(layer.id)
        }
        const sourceId = layer.id + '-source'
        if (map.getSource(sourceId)) {
          map.removeSource(sourceId)
        }
      })

      for (const hazardLayerId of shownHazardLayers) {
        // Add raster hazard layers if not present
        const rasterLayer: RasterLayer = {
          id: hazardLayerId,
          type: 'raster',
          source: hazardLayerId,
          layout: { visibility: 'visible' },
        }
        if (!map.getSource(hazardLayerId)) {
          map.addSource(hazardLayerId, { type: 'raster', url: `mapbox://${hazardLayerId}` })
        }
        if (!map.getLayer(hazardLayerId)) {
          map.addLayer(rasterLayer)
        }
      }

      const sourceId = propertyLayerId + '-source'
      if (!mapLayers.find((layer) => layer.id === propertyLayerId + '-stroke')) {
        if (!map.getSource(sourceId)) {
          map.addSource(sourceId, {
            type: 'geojson',
            data: selectedAssetFeature as GeoJSON.Feature<
              GeoJSON.Geometry,
              GeoJSON.GeoJsonProperties
            >,
          })
        }

        map.addLayer({
          id: `${propertyLayerId}-stroke`,
          type: 'line',
          source: sourceId,
          layout: {
            visibility: 'visible',
          },
          paint: {
            'line-color': theme.palette.uintel.main,
            'line-width': 4,
          },
        })
      } else {
        ;(map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(
          selectedAssetFeature as GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>,
        )
      }

      // bring to front
      map.moveLayer(`${propertyLayerId}-stroke`)
      if (map) await controlMouseEvents(map)
    }
    if (!map || !selectedAssetFeature) return
    addMapLayersAndEvents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selectedAssetFeature, shownHazardLayers])

  const mapOverlayRef = useRef<HTMLDivElement>(null)

  return (
    <>
      <GenericMapView
        map={map}
        setMapInstance={setMapInstance}
        customStyle={css`
          & .mapboxgl-ctrl-top-right .mapboxgl-ctrl {
            margin-top: ${(mapTopRightControlsRef.current?.clientHeight ?? 0) + 16}px;
          }
          & div.mapboxgl-popup-content {
            width: auto !important;
          }
        `}
        mapPaddingRef={mapPaddingRef}
      />

      <Box ref={mapOverlayRef} css={mapOverlayContainer}>
        <Box ref={mapTopRightControlsRef} css={mapTopRightContainer}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}
          >
            <Box
              sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '8px' }}
            >
              <PropertySearch size="small" searchOnClickSuggestion={true} />
              <PropertyMapTools mapOverlayRef={mapOverlayRef} />
            </Box>
          </Box>
        </Box>
        <Box css={mapBottomLeftContainer()}>
          <BaseMapButton />
        </Box>
        <Box css={mapBottomRightContainer({ theme })}>
          <MapLegends legendIds={legendIds} />
        </Box>
      </Box>
    </>
  )
}
